import { useState } from "react";
import { DimensionalGoldButton, GhostButton } from "./components/v2/ActionRow";
import Wordmark from "./components/v2/Wordmark";
import {
  isValidCheckoutEmail,
  isValidGiftCode,
  normalizeGiftCode,
} from "./purchase-recovery";
import "./purchase-recovery.css";

function RecoveryShell({ children, labelledBy, isStatus = false }) {
  return (
    <main className="purchase-recovery" aria-labelledby={labelledBy}>
      <div
        className="purchase-recovery__scene"
        style={{
          "--purchase-recovery-scene":
            'url("/backgrounds/tgc-124/threshold-continuation-v1.png")',
        }}
        aria-hidden="true"
      />
      <section
        className="purchase-recovery__card"
        role={isStatus ? "status" : undefined}
        aria-live={isStatus ? "polite" : undefined}
      >
        <Wordmark
          variant="stacked"
          markSrc="/brand/approved/tgc-conversation-loop-duotone.png"
          markClassName="tgc-v2-wordmark__sparkle--brand"
        />
        {children}
      </section>
    </main>
  );
}

export function PurchaseRecoveryRequest({ onBack, notice = "" }) {
  const [giftCode, setGiftCode] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const normalizedCode = normalizeGiftCode(giftCode);
    if (!isValidGiftCode(normalizedCode) || !isValidCheckoutEmail(email)) {
      setError("Enter your TGC Gift Code and the email used at checkout.");
      return;
    }

    setSending(true);
    setError("");
    try {
      const response = await fetch("/api/request-recovery", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ giftCode: normalizedCode, email: email.trim() }),
      });
      if (!response.ok) throw new Error("Recovery request failed");
      setSent(true);
    } catch {
      setError("We couldn't request a recovery email. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <RecoveryShell labelledBy="purchase-recovery-heading">
        <p className="purchase-recovery__eyebrow">PURCHASE RECOVERY</p>
        <h1 id="purchase-recovery-heading">Check your email.</h1>
        <p>
          If those details match a TGC purchase, we sent a secure continuation
          link. It expires in 30 minutes.
        </p>
        <p className="purchase-recovery__delivery-note">
          Open the secure link from that email to continue. If it doesn’t arrive
          soon, check your Spam or Junk folder.
        </p>
        <GhostButton onClick={onBack}>Back to the home page</GhostButton>
      </RecoveryShell>
    );
  }

  return (
    <RecoveryShell labelledBy="purchase-recovery-heading">
      <p className="purchase-recovery__eyebrow">ALREADY PAID?</p>
      <h1 id="purchase-recovery-heading">Continue your gift.</h1>
      <p>
        Enter the TGC Gift Code from your confirmation and the email used at
        checkout. We’ll send a secure link—no new payment required.
      </p>
      {notice && <p className="purchase-recovery__notice">{notice}</p>}
      <form className="purchase-recovery__form" onSubmit={handleSubmit} noValidate>
        <label htmlFor="purchase-gift-code">TGC Gift Code</label>
        <input
          id="purchase-gift-code"
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck="false"
          value={giftCode}
          onChange={(event) => setGiftCode(event.target.value.slice(0, 15))}
          onBlur={() => setGiftCode(normalizeGiftCode(giftCode))}
          aria-describedby={error ? "purchase-recovery-error" : undefined}
          placeholder="TGC-7M4K-P9Q2"
        />
        <label htmlFor="purchase-email">Checkout email</label>
        <input
          id="purchase-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck="false"
          value={email}
          onChange={(event) => setEmail(event.target.value.slice(0, 320))}
          aria-describedby={error ? "purchase-recovery-error" : undefined}
          placeholder="you@example.com"
        />
        {error && (
          <p id="purchase-recovery-error" className="purchase-recovery__error" role="alert">
            {error}
          </p>
        )}
        <DimensionalGoldButton type="submit" disabled={sending} data-disabled={sending ? "true" : "false"}>
          {sending ? "Sending…" : "Email my secure link"}
        </DimensionalGoldButton>
        <GhostButton onClick={onBack}>Back</GhostButton>
      </form>
      <a className="purchase-recovery__support" href="mailto:support@thegiftconversation.com">
        I don’t know my Gift Code
      </a>
    </RecoveryShell>
  );
}

export function PurchaseAccessScreen({
  purchase,
  hasDraft,
  isInitialCheckout = false,
  onContinue,
  onStartOver,
  onBack,
}) {
  const unavailable = ["refund_blocked", "refunded", "expired"].includes(
    purchase?.status
  );

  if (unavailable) {
    const copy = purchase.status === "refunded"
      ? "This purchase has been refunded, so its gift content is no longer available."
      : purchase.status === "refund_blocked"
        ? "We found the purchase, but its automatic refund needs support review."
        : "This gift has passed its hosted-retention window and is no longer available.";
    return (
      <RecoveryShell labelledBy="purchase-access-heading">
        <p className="purchase-recovery__eyebrow">{purchase.giftCode}</p>
        <h1 id="purchase-access-heading">Purchase status</h1>
        <p>{copy}</p>
        <a className="purchase-recovery__support" href="mailto:support@thegiftconversation.com">
          Contact support
        </a>
        <GhostButton onClick={onBack}>Back to the home page</GhostButton>
      </RecoveryShell>
    );
  }

  return (
    <RecoveryShell labelledBy="purchase-access-heading">
      <p className="purchase-recovery__eyebrow">
        {isInitialCheckout ? "PAYMENT CONFIRMED" : "PURCHASE RESTORED"}
      </p>
      <h1 id="purchase-access-heading">
        {hasDraft ? "Pick up where you left off." : "Your gift is ready to begin."}
      </h1>
      <p>Your TGC Gift Code</p>
      <strong className="purchase-recovery__code">{purchase.giftCode}</strong>
      <p className="purchase-recovery__code-note">
        Keep this code. It restores this purchase without charging you again.
      </p>
      <div className="purchase-recovery__actions">
        {hasDraft ? (
          <DimensionalGoldButton onClick={onContinue}>
            Continue where I left off
          </DimensionalGoldButton>
        ) : (
          <DimensionalGoldButton onClick={onStartOver}>
            Begin this gift
          </DimensionalGoldButton>
        )}
        {hasDraft && (
          <GhostButton onClick={onStartOver}>Start this gift over</GhostButton>
        )}
      </div>
    </RecoveryShell>
  );
}

export function PurchaseRecoveryLoading() {
  return (
    <RecoveryShell labelledBy="purchase-loading-heading" isStatus>
      <p className="purchase-recovery__eyebrow">PURCHASE RECOVERY</p>
      <h1 id="purchase-loading-heading">Restoring your gift…</h1>
      <p>This should only take a moment.</p>
    </RecoveryShell>
  );
}
